import { ApolloQueryResult } from '@apollo/client';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import React, { Fragment, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Button from '@atlaskit/button';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import { R400 } from '@atlaskit/theme/colors';
import Tooltip from '@atlaskit/tooltip';
import { EntitlementDetails, LinkedDestinationEntitlement } from '@css-xp/graphtypes/src/generated-types-new';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { EntitlementBaseProduct } from '@atlassian/cssxp-types-entitlement';
import { CLOUD_SITE_PATH, CLOUD_SITE_PATH_2, Permissions, SourceSystem } from '@atlassian/cssxp-typescommon';
import { isDeletedEntitlement, isOfferingArchived, isOfferingAtNotice } from '@atlassian/cssxp-utils-entitlement';
import { ENABLE_CLOUD_SITE_AC_REDIRECTION, ENABLE_SANDBOX_ENTITLEMENT } from '@atlassian/cssxp-utils-generic';
import { isStatuspage } from '@atlassian/cssxp-utils-product';
import { ApprovalType, useApprovalDeskPermissions } from '@css-xp/apd-common';
import { ApprovalStatus, useTaskList } from '@css-xp/apd-task-manager';
import {
  CopyableText,
  ErrorComponents,
  Icon,
  NotificationTypes,
  OpenSidebarPayload,
  SummarySectionV2,
  commonContexts,
  errorNotificationBuilder,
  getCASSIBaseURL,
  isAccess,
  useFeatureToggle,
  useNotification,
  useSidebar,
} from '@css-xp/base';
import { SIDEBAR } from '../../../entitlement-page/src/components/entitlement-page';
import { daysForTrialExpiry, getTrialEndDate } from '../../entitlement-page-utils';
import {
  GetOfferingName,
  getBillingCycle,
  getTotalDaysInTrial,
  getTrialDate,
  isStatusPageTrial,
  isTrialEntitlement,
} from '../entitlment-summary-utils';
import { PromotionsSummary } from '../promotions-summary';
import {
  NextPriceEstimateSection,
  PageUrlSection,
  PricingSection,
  SandboxParentSection,
  StatusSection,
  UUIDSection,
  UserTier,
  UsersSection,
} from './sections';

export const TrialTemplate = ({
  entitlement,
  linkedEntitlements,
  refetch,
}: {
  entitlement: EntitlementDetails;
  linkedEntitlements: LinkedDestinationEntitlement[];
  refetch: () => Promise<ApolloQueryResult<unknown>>;
}) => {
  const { openSidebar } = useSidebar();
  const {
    cloudSiteHostname,
    showExtendTrial,
    activeSubscriptionBillingGroupId,
    billingGroupId,
    migrated,
  } = entitlement;

  const activeBillingGroupId = activeSubscriptionBillingGroupId ?? billingGroupId;
  const { data, getTransactionAccount } = React.useContext(commonContexts.TransactionAccountContext);
  const transactionAccount = getTransactionAccount(data);

  let invoiceGroup: any = {};
  if (activeBillingGroupId) {
    invoiceGroup = transactionAccount?.availableInvoiceGroups?.nodes.find(
      (invoiceGroupEle) => activeBillingGroupId === invoiceGroupEle?.id
    );
  }

  const isStatusPage = entitlement.baseProduct === EntitlementBaseProduct.STATUS_PAGE;
  const isTrial = isTrialEntitlement(entitlement, isStatusPage);
  const isFreePlan = entitlement?.edition && entitlement?.edition.includes('Free');
  const totalDaysInTrial = getTotalDaysInTrial(entitlement.subscription, entitlement.offeringId as string);
  const promotions = entitlement.promotions?.length || 0;

  const { isTrialExtApprovalFlowEnabled } = useApprovalDeskPermissions();
  const { loading: taskListLoading, error: taskListError, hasOpenApprovalRequest, approvalStatus } = useTaskList({
    businessKey: entitlement.entitlementNumber,
    skip: !isTrialExtApprovalFlowEnabled || !isTrial,
    approvalType: ApprovalType.TRIAL_EXTENSION,
  });

  const { setNotificationSent, sendErrorNotification } = useNotification();
  const [isEnabled] = useFeatureToggle();

  const isSandbox = isEnabled(ENABLE_SANDBOX_ENTITLEMENT) && entitlement?.isSandbox;
  const sitePath = migrated?.toUpperCase() === SourceSystem.CCP ? CLOUD_SITE_PATH_2 : CLOUD_SITE_PATH;

  const trialExtensionRequestSummary = useMemo(() => {
    // for sandbox or deleted entitlement, return null
    if (isSandbox || isDeletedEntitlement(entitlement)) {
      return null;
    }
    if (taskListLoading) {
      return (
        <div className="trial-extension-footer-loader">
          <LoadingIndicator size="medium" />
        </div>
      );
    }

    if (taskListError) {
      return (
        <div data-testid="trial-extension-request-error" className="trial-extension-request-error">
          <div className="error-toast-icon">
            <ErrorIcon key={'error'} label="Error" primaryColor={R400} size="medium" />
          </div>
          <div className="error-text">Something went wrong</div>
        </div>
      );
    }

    if (hasOpenApprovalRequest) {
      return (
        <>
          {approvalStatus && (
            <div>
              <ApprovalStatus status={approvalStatus} />
            </div>
          )}
          <Link to={{ hash: '#approvals' }} className="inner-text">
            View request
          </Link>
        </>
      );
    } else {
      if (!isFreePlan && showExtendTrial && isAccess(Permissions.SUBSCRIPTION_TRAIL_LENGTH_DAYS_WRITE)) {
        const isExtendAppTrialDisabled =
          entitlement?.isApp && (isOfferingArchived(entitlement) || isOfferingAtNotice(entitlement));
        const isStatusPagePublicTrial = isStatusPage && entitlement?.edition?.toLowerCase().includes('public trial');
        const isExtendTrialDisabled = isExtendAppTrialDisabled || isStatusPagePublicTrial;

        return (
          <Button
            testId="extend-trial-btn"
            appearance="link"
            className="inner-text"
            aria-label="Extend Trial"
            onClick={() => {
              handleSidebarAction({
                sidebarType: SIDEBAR.EXTEND_TRIAL,
                payload: {
                  entitlement,
                  refetch,
                },
                sidebarOptions: {
                  isOverlay: true,
                },
              });
            }}
            isDisabled={isExtendTrialDisabled}
          >
            Extend
          </Button>
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasOpenApprovalRequest, taskListLoading, taskListError, isFreePlan, approvalStatus, entitlement]);

  const getTrialStartDate = () => {
    if (isFreePlan) {
      return '';
    }
    if (isTrial) {
      if (!isStatusPage && entitlement?.subscriptionTimestamps?.firstTrialStartTimestamp) {
        return entitlement?.subscriptionTimestamps?.firstTrialStartTimestamp;
      }
      if (isStatusPageTrial(entitlement) && entitlement?.latestPeriodStartDate) {
        return entitlement?.latestPeriodStartDate;
      }
    }
    return '';
  };

  const renderTotalDaysInTrial = () => {
    if (isFreePlan) {
      return <span className="data-unavailable">0</span>;
    }
    {
      return isStatusPage ? (
        isStatusPageTrial(entitlement) && entitlement.latestPeriodEndDate && entitlement.latestPeriodStartDate ? (
          <CopyableText>
            <span>
              {differenceInCalendarDays(
                parseISO(entitlement.latestPeriodEndDate),
                parseISO(entitlement.latestPeriodStartDate)
              )}
            </span>
          </CopyableText>
        ) : (
          <span className="data-unavailable">0</span>
        )
      ) : totalDaysInTrial ? (
        <CopyableText>
          <span>{totalDaysInTrial}</span>
        </CopyableText>
      ) : (
        <span className="data-unavailable">0</span>
      );
    }
  };

  const handleSidebarAction = (openSidebarPayload: OpenSidebarPayload) => {
    openSidebar(openSidebarPayload);
  };

  const cassiBaseURL = getCASSIBaseURL();

  useEffect(() => {
    if (taskListError) {
      const notificationOptions = errorNotificationBuilder()
        .withId('trial-extension-request-notification-error')
        .withHeader('Error')
        .withMessage(
          <p>
            An error occurred while contacting approval desk - try again later. Still having trouble? Contact{' '}
            {<ErrorComponents.ChatRoomLink />}
          </p>
        )
        .withDismissAfter(3000)
        .isVersion(NotificationTypes.FLAG)
        .build();
      sendErrorNotification(notificationOptions);
      setNotificationSent(true);
    }
  }, [taskListError]);

  return (
    <Fragment>
      <SummarySectionV2.SummaryGridCalloutItem testId="summary-cloud-site">
        <div className="billing-cycle title-height">
          <label>Cloud URL</label>
          <div className="entitlement-details-column copy-link">
            {cloudSiteHostname ? (
              isEnabled(ENABLE_CLOUD_SITE_AC_REDIRECTION) ? (
                <CopyableText duration={0}>
                  <Link to={`${sitePath}/${entitlement.cloudSiteId}`}>{cloudSiteHostname}</Link>
                </CopyableText>
              ) : (
                <CopyableText duration={0}>
                  <Link
                    to={{ pathname: `${cassiBaseURL}${CLOUD_SITE_PATH}/${entitlement.cloudSiteId}` }}
                    target="_blank"
                  >
                    {cloudSiteHostname}
                  </Link>
                </CopyableText>
              )
            ) : (
              <span data-testid="empty-cloud-site" className="data-unavailable">
                N/A
              </span>
            )}
          </div>
        </div>
      </SummarySectionV2.SummaryGridCalloutItem>

      <SummarySectionV2.SummaryGridCalloutItem testId="summary-billing-cycle">
        <div className="billing-cycle title-height">
          <label>Billing cycle</label>
          {getBillingCycle(entitlement)}
        </div>
      </SummarySectionV2.SummaryGridCalloutItem>

      <SummarySectionV2.SummaryGridCalloutItem testId="summary-offering-name">
        <div className="billing-plan billing-cycle title-height">
          <label>Plan</label>
          <GetOfferingName entitlement={entitlement} refetch={refetch} />
        </div>
      </SummarySectionV2.SummaryGridCalloutItem>

      <SummarySectionV2.SummaryGridCalloutItem testId="summary-trial-start-date">
        <div className="billing-plan billing-cycle title-height" data-testid="summary-trial-start-date-label">
          <label>Trial start date</label>
          <div className="entitlement-details-column" data-testid="summary-trial-start-date-value">
            {getTrialDate(getTrialStartDate())}
          </div>
        </div>
      </SummarySectionV2.SummaryGridCalloutItem>

      <SummarySectionV2.SummaryGridCalloutItem testId="summary-trial-end-date">
        <div className="billing-plan billing-cycle title-height" data-testid="summary-trial-end-date-label">
          <label>
            {entitlement?.isApp && (isOfferingArchived(entitlement) || isDeletedEntitlement(entitlement))
              ? 'Subscription end date'
              : 'Trial end date'}
          </label>
          <div className="entitlement-details-column" data-testid="summary-trial-end-date-value">
            {getTrialDate(getTrialEndDate(entitlement))}
          </div>
          {(!invoiceGroup || !invoiceGroup?.defaultPaymentMethod) && daysForTrialExpiry(entitlement) < 4 && (
            <Tooltip content="The linked billing profile is incomplete, so this trial can't be converted to a paid subscription">
              <label data-testid="billing-issues-label" className="info-icon">
                <Icon type="warning" /> Billing issues
              </label>
            </Tooltip>
          )}
        </div>
      </SummarySectionV2.SummaryGridCalloutItem>

      <SummarySectionV2.SummaryGridCalloutItem testId="summary-total-daye-in-trial-name">
        <div className="billing-plan billing-cycle title-height" data-testid="summary-total-daye-in-trial-label">
          <label>Total days on trial</label>
          <div className="entitlement-details-column">{renderTotalDaysInTrial()}</div>
        </div>
      </SummarySectionV2.SummaryGridCalloutItem>

      <SummarySectionV2.SummaryGridCalloutItem testId="summary-trial-extensions-request">
        <div className="billing-plan billing-cycle title-height" data-testid="summary-trial-extensions-request-label">
          <label>Trial extensions</label>
          <div className="entitlement-details-column">{trialExtensionRequestSummary}</div>
        </div>
      </SummarySectionV2.SummaryGridCalloutItem>

      <PricingSection entitlement={entitlement} linkedEntitlements={linkedEntitlements} refetch={refetch} />
      {isStatuspage(entitlement?.productId) && <PageUrlSection entitlement={entitlement} />}
      {!isStatuspage(entitlement?.productId) && (
        <UsersSection linkedEntitlements={linkedEntitlements} entitlement={entitlement} />
      )}
      <UserTier entitlement={entitlement} />
      <NextPriceEstimateSection entitlement={entitlement} />
      <SummarySectionV2.SummaryGridCalloutItem testId="summary-active-promotions">
        <PromotionsSummary entitlementNumber={entitlement.entitlementNumber} promotions={promotions} />
      </SummarySectionV2.SummaryGridCalloutItem>
      <UUIDSection entitlement={entitlement} />
      <StatusSection entitlement={entitlement} />
      <SandboxParentSection entitlement={entitlement} />
    </Fragment>
  );
};
